import React from "react";
import { observer } from "mobx-react";

import { SelectFieldOption } from "./selectFieldOption";
import MultiSelectWithDoDOrdering from "../../../general/MultiSelectWithDoDOrdering";
import FilterItem from "./FilterItem";
import "./FilterItem.scss";
import { CUBE_QUERY_WIZARD_SQL_QUERY_PART } from "../../../constants/constants-container";

/**
 * @typedef {import("../../../stores/CubeQueryWizard/CubeQueryWizardStore").default} CubeQueryWizardStore
 */

/**
 * Section of QueryWizard that is responsible for "WHERE" in SQL
 */
@observer
class FilterComponent extends React.Component {
  handleFilterOperatorChange = (event, index) => {
    const value = event.target.value;
    this.props.store.changeFilterOperator(index, value);
  };

  handleFilterOperatorStartChange = (event) => {
    const value = event.target.value;
    this.props.store.filterOperatorStart = value;
  };

  handleFilterOperatorEndChange = (event) => {
    const value = event.target.value;
    this.props.store.filterOperatorEnd = value;
  };

  componentDidMount = () => {
    const store = this.props.store;
    store.populateDefaultFiltersFromCubes();
  };

  render() {
    /**
     * @type CubeQueryWizardStore
     */
    const store = this.props.store;
    const shouldWarnAboutDataPointsMandatory =
      store.shouldWarnAboutDataPointsMandatory;
    return (
      <section>
        <h4>
          Filter
          {store.hasTemplateRepresentation || store.hasCombinations
            ? " by Attributes"
            : ""}
          :
        </h4>
        {store.hasCombinations && (
          <div className="alert alert-warning" role="alert">
            <span
              className="glyphicon glyphicon-info-sign"
              aria-hidden="true"
            ></span>{" "}
            <span className="sr-only">Info:</span>
            This cube contains combinations. Therefore, it is not possible to
            choose a different conjunction besides AND and it is not possible to
            pick the same field multiple times.
          </div>
        )}
        {store.filterByVariableDisabled && (
          <div className="alert alert-warning" role="alert">
            <span
              className="glyphicon glyphicon-info-sign"
              aria-hidden="true"
            ></span>{" "}
            <span className="sr-only">Info:</span>
            Delete value in filter by Series Keys to enable filter by
            attributes.
          </div>
        )}
        {shouldWarnAboutDataPointsMandatory && (
          <div className="alert alert-warning" role="alert">
            <span
              className="glyphicon glyphicon-info-sign"
              aria-hidden="true"
            ></span>{" "}
            Filters on logical attributes will only be applied in the selection
            of data points.
          </div>
        )}
        <div className="CubeQueryWizard__filter-container">
          {!store.hasCombinations && store.filterOperators.length > 1 && (
            <div className="CubeQueryWizard__filter-operators-placeholder"></div>
          )}
          <div>
            {!store.hasCombinations && store.filterOperators.length > 1 && (
              <div className="CubeQueryWizard__row item operator">
                <div className="CubeQueryWizard__filter-operator start">
                  <input
                    value={store.filterOperatorStart}
                    className="form-control"
                    onChange={this.handleFilterOperatorStartChange}
                    placeholder="SQL ..."
                  ></input>
                </div>
              </div>
            )}
            <MultiSelectWithDoDOrdering
              disabled={store.filterByVariableDisabled}
              options={store.variablesSelectOptions(
                CUBE_QUERY_WIZARD_SQL_QUERY_PART.FILTER_FIELDS
              )}
              order={store.filterOrder}
              values={store.selectedFilterItemIndexes}
              placeholder={
                store.loadingCombinations
                  ? "Disabled while loading combinations..."
                  : "Select attributes to filter..."
              }
              allowSelectMultipleTimes={!store.hasCombinations}
              onChangeOrder={store.changeFilterOrder}
              onAdd={store.addFilterItem}
              onDelete={store.deleteAllFilterItems}
              components={{ Option: SelectFieldOption }}
            >
              {store.filterOrder.map((itemID, index) => (
                <div
                  key={itemID}
                  id={itemID}
                  className="CubeQueryWizard__row item FilterItem"
                >
                  {!store.hasCombinations && index > 0 && (
                    <div className="CubeQueryWizard__filter-operator">
                      <input
                        value={store.filterOperators[index]}
                        className="form-control"
                        onChange={(e) =>
                          this.handleFilterOperatorChange(e, index)
                        }
                        placeholder="SQL ..."
                      ></input>
                    </div>
                  )}
                  <div
                    className="x-icon"
                    aria-hidden="true"
                    onClick={() => store.deleteFilter(itemID)}
                  >
                    ×
                  </div>
                  <FilterItem
                    cubeName={
                      store.getCubeDetailsByItemKey(
                        store.filterState[itemID].itemIndex
                      ).name
                    }
                    isPrimaryCube={
                      store.getCubeDetailsByItemKey(
                        store.filterState[itemID].itemIndex
                      ).cubeId === store.cubeDetails.cubeId
                    }
                    hasForeignCubes={store.hasForeignCubes}
                    item={
                      store.cubeStructureItems[
                        store.filterState[itemID].itemIndex
                      ]
                    }
                    itemID={itemID}
                    getEqualityOption={store.filterState[itemID].equalityOption}
                    kind={store.filterState[itemID].kind}
                    values={store.filterState[itemID].values}
                    allowedMembers={store.getAllowedMembers(itemID)}
                    disabled={store.isFilterItemDisabled(itemID)}
                    onUpdateFilterItemValues={store.updateFilterItemValues}
                    onBlur={store.unfocusFilterItem}
                    onFocus={store.focusFilterItem}
                    loading={
                      store.loadingCombinations ||
                      store.filterState[itemID].loading
                    }
                  />
                </div>
              ))}
            </MultiSelectWithDoDOrdering>
            {!store.hasCombinations && store.filterOperators.length > 1 && (
              <div className="CubeQueryWizard__row item operator">
                <div className="CubeQueryWizard__filter-operator end">
                  <input
                    value={store.filterOperatorEnd}
                    className="form-control"
                    onChange={this.handleFilterOperatorEndChange}
                    placeholder="SQL ..."
                  ></input>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export const Filter = observer(FilterComponent);
